import {useEffect, useState} from "react";
import {App as AntdApp, Alert, Empty, Popconfirm, Tag, Tooltip} from "antd";
import {useForm, Controller} from "react-hook-form";
import {Button, FontAwesomeIcon, Loading, ModalForm} from "~/components";
import {InputField, SelectField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetContentTargetsQuery,
	useProbeContentTargetMutation,
	useCreateContentTargetMutation,
	useUpdateContentTargetMutation,
	useDeleteContentTargetMutation,
} from "~/reduxs/api/contentApiSlice";

/** Metadata theo LOẠI kênh — field auth động + hướng dẫn lấy credential (Đợt 4). */
const PLATFORMS = {
	wordpress: {
		label: 'WordPress', icon: 'fa-brands fa-wordpress',
		urlPlaceholder: 'https://blog.cuahang.vn',
		fields: [
			{name: 'username', label: 'Username WordPress', placeholder: 'admin'},
			{name: 'app_password', label: 'Application Password', placeholder: 'xxxx xxxx xxxx xxxx xxxx xxxx'},
		],
		hint: 'Tạo trong wp-admin → Users → Profile → mục Application Passwords (không phải mật khẩu đăng nhập).',
	},
	sapo: {
		label: 'SAPO', icon: 'fa-light fa-store',
		urlPlaceholder: 'https://cuahang.mysapo.net',
		fields: [
			{name: 'api_key', label: 'API key (private app)', placeholder: 'API key'},
			{name: 'secret', label: 'Secret (private app)', placeholder: 'Secret key'},
		],
		hint: 'Tạo private app trong trang quản trị Sapo → Ứng dụng → Private app; URL dùng tên miền mysapo.net của cửa hàng.',
	},
	haravan: {
		label: 'Haravan', icon: 'fa-light fa-store',
		urlPlaceholder: 'https://cuahang.myharavan.com',
		fields: [
			{name: 'token', label: 'Access token (private app)', placeholder: 'Access token'},
		],
		hint: 'Tạo private app trong quản trị Haravan → Ứng dụng để lấy access token.',
	},
	shopify: {
		label: 'Shopify', icon: 'fa-brands fa-shopify',
		urlPlaceholder: 'https://cuahang.myshopify.com',
		fields: [
			{name: 'token', label: 'Admin API access token (custom app)', placeholder: 'shpat_...'},
		],
		hint: 'Tạo custom app trong Shopify admin → Settings → Apps and sales channels → Develop apps (cần scope write_content).',
	},
};

const TYPE_OPTIONS = Object.entries(PLATFORMS).map(([value, p]) => ({value, label: p.label}));

const STATUS_OPTIONS = [
	{value: 'draft', label: 'Đăng dạng NHÁP (an toàn — vào kênh duyệt rồi mới public)'},
	{value: 'publish', label: 'Đăng CÔNG KHAI ngay'},
];

const EMPTY = {type: 'wordpress', name: '', base_url: '', username: '', app_password: '', token: '', api_key: '', secret: '', default_status: 'draft', blog_id: null};

/**
 * Modal kết nối kênh (Đợt 4 — đa nền tảng): chọn loại → field credential động →
 * WordPress lưu thẳng (BE verify khi lưu); Shopify-like thêm bước PROBE lấy danh sách
 * blog của cửa hàng để chọn blog đích rồi mới lưu.
 */
function TargetModal({projectId, open, onCancel, onSaved}) {

	const {message} = AntdApp.useApp();
	const [blogs, setBlogs] = useState(null); // null = chưa probe · [] = đã probe (bước chọn blog)

	const [probe, {isLoading: probing}] = useProbeContentTargetMutation();
	const [create, {isLoading: creating}] = useCreateContentTargetMutation();

	const {control, handleSubmit, watch, reset} = useForm({defaultValues: EMPTY});

	useEffect(() => {
		if (open) {
			reset(EMPTY);
			setBlogs(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const type = watch('type');
	const platform = PLATFORMS[type] || PLATFORMS.wordpress;
	const chooseBlog = blogs !== null;

	const onSubmit = async (values) => {
		// Validate tay (field động theo loại kênh).
		if (!values.base_url?.trim()) return message.warning('Nhập URL website/cửa hàng.');
		for (const f of platform.fields) {
			if (!values[f.name]?.trim()) return message.warning('Nhập ' + f.label + '.');
		}

		try {
			// Shopify-like: bước 1 probe lấy blog; bước 2 (đã có blogs) lưu với blog_id.
			if (values.type !== 'wordpress' && !chooseBlog) {
				const res = await probe({projectId, ...values}).unwrap();
				const list = res?.blogs || [];
				if (!list.length) {
					return message.warning('Cửa hàng chưa có blog nào — tạo blog trong trang quản trị trước.');
				}
				setBlogs(list);
				return;
			}

			if (values.type !== 'wordpress' && !values.blog_id) {
				return message.warning('Chọn blog đích để đăng bài.');
			}

			const chosen = (blogs || []).find((b) => b.id === values.blog_id);
			await create({
				projectId,
				...values,
				blog_handle: chosen?.handle || '',
			}).unwrap();
			message.success('Đã kết nối kênh ' + platform.label);
			onSaved?.();
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	return (
		<ModalForm
			open={open}
			icon={platform.icon}
			title={chooseBlog ? 'Chọn blog đích' : 'Kết nối kênh đăng'}
			subtitle={chooseBlog
				? 'Bài viết sẽ được đăng vào blog này của cửa hàng'
				: 'Hệ thống kiểm tra kết nối trước khi lưu; credential được mã hoá'}
			onCancel={chooseBlog ? () => setBlogs(null) : onCancel}
			cancelText={chooseBlog ? 'Quay lại' : 'Huỷ'}
			onOk={handleSubmit(onSubmit)}
			okText={chooseBlog ? 'Lưu kênh' : (type === 'wordpress' ? 'Kiểm tra & lưu kênh' : 'Kiểm tra kết nối')}
			loading={probing || creating}
		>
			{!chooseBlog && (
				<>
					<Controller control={control} name="type" render={({field}) => (
						<SelectField label="Nền tảng" options={TYPE_OPTIONS} style={{width: '100%'}} {...field} />
					)} />
					<Controller control={control} name="base_url" render={({field}) => (
						<InputField label={type === 'wordpress' ? 'URL website' : 'URL cửa hàng'} placeholder={platform.urlPlaceholder} {...field} />
					)} />
					<Controller control={control} name="name" render={({field}) => (
						<InputField label="Tên kênh (tuỳ chọn)" placeholder="vd: Blog chính" {...field} />
					)} />
					{platform.fields.map((f) => (
						<Controller key={f.name} control={control} name={f.name} render={({field}) => (
							<InputField label={f.label} placeholder={f.placeholder} {...field} />
						)} />
					))}
					<p style={{fontSize: 12.5, color: '#6b7280', margin: '-4px 0 10px'}}>{platform.hint}</p>
					<Controller control={control} name="default_status" render={({field}) => (
						<SelectField label="Chế độ đăng mặc định" options={STATUS_OPTIONS} style={{width: '100%'}} {...field} />
					)} />
				</>
			)}

			{chooseBlog && (
				<Controller control={control} name="blog_id" render={({field}) => (
					<SelectField
						label={`Blog đích trên ${platform.label} (${(blogs || []).length} blog)`}
						options={(blogs || []).map((b) => ({value: b.id, label: b.title + (b.handle ? ` (/blogs/${b.handle})` : '')}))}
						style={{width: '100%'}}
						{...field}
					/>
				)} />
			)}
		</ModalForm>
	);
}

/**
 * Tab "Kênh đăng" (Đợt 3+4 — GĐ2): quản lý kênh xuất bản của dự án — WordPress /
 * SAPO / Haravan / Shopify (driver adapter). Kênh lỗi 5 lần liên tiếp tự tắt — bật lại tại đây.
 */
function ContentTargetsTab({projectId}) {

	const {message} = AntdApp.useApp();
	const [openModal, setOpenModal] = useState(false);

	const {data, isLoading} = useGetContentTargetsQuery(projectId);
	const [updateTarget] = useUpdateContentTargetMutation();
	const [deleteTarget] = useDeleteContentTargetMutation();

	if (isLoading) return <Loading />;

	const targets = data?.targets || [];
	const available = Boolean(data?.available);

	const enable = async (t) => {
		try {
			await updateTarget({projectId, targetId: t.id, status: 'active'}).unwrap();
			message.success('Đã bật lại kênh');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const remove = async (t) => {
		try {
			await deleteTarget({projectId, targetId: t.id}).unwrap();
			message.success('Đã xoá kênh');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	return (
		<div>
			{!available && (
				<Alert type="warning" showIcon style={{marginBottom: 12}}
					message="Chưa khởi tạo bảng dữ liệu"
					description="Chạy GET api/utils/database để tạo bảng publish_targets trước khi kết nối kênh." />
			)}

			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
				<Button primary small disabled={!available}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-plug-circle-plus" />}
					onClick={() => setOpenModal(true)}>
					Kết nối kênh
				</Button>
			</div>

			{targets.length === 0 ? (
				<Empty description="Chưa có kênh đăng — kết nối WordPress / SAPO / Haravan / Shopify để đăng bài trực tiếp từ đây." />
			) : (
				<div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12}}>
					{targets.map((t) => {
						const platform = PLATFORMS[t.type] || {label: t.type, icon: 'fa-light fa-globe'};
						return (
							<div key={t.id} className="app-card" style={{padding: 16}}>
								<div style={{display: 'flex', justifyContent: 'space-between', gap: 8}}>
									<div>
										<div style={{fontWeight: 600}}>
											<FontAwesomeIcon icon={platform.icon} /> {t.name} <Tag>{platform.label}</Tag>
										</div>
										<div style={{fontSize: 12.5, color: '#6b7280'}}>{t.base_url}</div>
									</div>
									{t.status === 'active'
										? <Tag color="success">Hoạt động</Tag>
										: <Tag color="error">Đã tắt</Tag>}
								</div>

								<div style={{fontSize: 12.5, color: '#6b7280', margin: '8px 0'}}>
									Mặc định: {t.default_status === 'publish' ? 'đăng công khai' : 'đăng nháp'}
									{t.last_error && (
										<Tooltip title={t.last_error}>
											<div style={{color: '#dc2626', marginTop: 4}}>
												<FontAwesomeIcon icon="fa-light fa-circle-exclamation" /> Lỗi gần nhất: {t.last_error.slice(0, 60)}...
											</div>
										</Tooltip>
									)}
								</div>

								<div style={{display: 'flex', gap: 12, fontSize: 13}}>
									{t.status !== 'active' && (
										<span style={{cursor: 'pointer', color: '#16a34a'}} onClick={() => enable(t)}>
											<FontAwesomeIcon icon="fa-light fa-power-off" /> Bật lại
										</span>
									)}
									<Popconfirm title="Xoá kênh đăng này?" okText="Xoá" cancelText="Huỷ" onConfirm={() => remove(t)}>
										<span style={{cursor: 'pointer', color: '#dc2626'}}>
											<FontAwesomeIcon icon="fa-light fa-trash-can" /> Xoá
										</span>
									</Popconfirm>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<TargetModal projectId={projectId} open={openModal} onCancel={() => setOpenModal(false)} onSaved={() => setOpenModal(false)} />
		</div>
	);
}

export default ContentTargetsTab;
